import { withTheme } from 'styled-components';
import TranslateIgnoreText from '@this/shared/text/translate_ignore_text';
import { isAiTravel, toDomesticTel } from '@this/src/util';
import MarketLogHelper from '@this/domain/market_log/market_log_helper';
import type Trip from '@this/domain/trip/trip2';
import A from '@this/shared/atoms/a';
import type TransportElementAttachFile from '@this/domain/transport_element_attach_file';
import PdfIcon from '@material-ui/icons/PictureAsPdf';
import React from 'react';
import { styled } from '@this/constants/themes';
import { media } from '@this/shared/atoms/media';
import type User from '@this/domain/user/user';
import TripCardHotelTypeLine from '@this/components/trips_management/trip_detail/trip_card_hotel_type_line';
import Box from '@material-ui/core/Box';
import ExternalLinkIcon from '@this/shared/icons/external_link_icon';
import { Link } from '@this/shared/ui/navigations/link';
import type OrderItem from '@this/domain/order_item2';
import type TravelerInformation from '@this/domain/traveler_information';
import HotelElement from '@this/domain/hotel_element2';
import TransportElement from '@this/domain/transport_element2';
import WifiElement from '@this/domain/wifi_element/wifi_element2';

type Props = {
  user: User;
  trip: Trip;
  orderItem: OrderItem;
  serviceId: number;
  theme: Theme;
};

interface TranslatedLine {
  label: string;
  value: string;
  translate: boolean;
}

interface Theme {
  themeClass: string;
  serviceName: string;
}

const OrderItemContent: React.FC<Props> = ({ user, trip, orderItem, serviceId, theme }) => {
  const sortTransportElementAttachFiles = (
    files: TransportElementAttachFile[],
    travelerInformations: TravelerInformation[],
    userId: number
  ): TransportElementAttachFile[] => {
    return files.slice().sort((a, b) => {
      const aUserIdMatch = travelerInformations.some(
        traveler => traveler.id.toString() === a.travelerInformationId && traveler.userId === userId
      );
      const bUserIdMatch = travelerInformations.some(
        traveler => traveler.id.toString() === b.travelerInformationId && traveler.userId === userId
      );
      // user_id が一致するファイルを先頭に、それ以外はid順
      if (aUserIdMatch && !bUserIdMatch) return -1;
      if (!aUserIdMatch && bUserIdMatch) return 1;
      return a.id - b.id;
    });
  };

  return (
    <>
      {orderItem.elements.map((element, elementIndex: number) => (
        <MainBlock key={element.id}>
          {!orderItem.isFollowingElement(element) && <BlockTitle>{element.title}</BlockTitle>}
          <BlockContent>
            {element instanceof HotelElement &&
              (element.isCanceled
                ? element.name && (
                    <>
                      <CanceledBlockTextL data-wovn-ignore>{element.name}</CanceledBlockTextL>
                      <CancelMessage>(キャンセル済み)</CancelMessage>
                    </>
                  )
                : element.name && <BlockTextL data-wovn-ignore>{element.name}</BlockTextL>)}
            {element instanceof TransportElement && (element.name || '')}
            <BlockTextM>
              <TranslateIgnoreText text={element.summary} />
            </BlockTextM>
            <BlockTextM>
              {element instanceof HotelElement ? (
                <>
                  <p>
                    返金：
                    {element.refundable === true ? (
                      <span>可</span>
                    ) : element.refundable === false ? (
                      <span>不可</span>
                    ) : (
                      <span>お問い合わせください</span>
                    )}
                  </p>
                  {element.structuredHotelTypeDetail().map((l: TranslatedLine) => {
                    return <TripCardHotelTypeLine key={l.label} line={l} />;
                  })}
                  {element.detailPath && (
                    <BlockTextM>
                      <span>宿泊詳細情報：</span>
                      <span>
                        <a href={element.detailPath} target="_blank" rel="noopener noreferrer">
                          詳細はコチラ
                        </a>
                      </span>
                    </BlockTextM>
                  )}
                  <p>
                    住所:{' '}
                    <Link
                      data-wovn-ignore
                      href={element.hotelMapAddressLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {element.address ? element.address : '所在地を確認'}
                    </Link>
                  </p>
                  <p>
                    {element.tel && (
                      <>
                        <span>電話番号：</span>
                        <span data-wovn-ignore>{toDomesticTel(element.tel)}</span>
                      </>
                    )}
                  </p>
                  <Room>
                    {element.structuredRoomTypeDetail().map((l: TranslatedLine, k: number) => {
                      return <TripCardHotelTypeLine key={`t_${k}`} line={l} />;
                    })}
                  </Room>
                  {/* ホテル予約情報 */}
                  {element.hotelReservationNumber && (
                    <>
                      <span>ホテル予約番号：</span>
                      <span data-wovn-ignore>{element.hotelReservationNumber}</span>
                    </>
                  )}
                </>
              ) : element instanceof TransportElement ? (
                <>
                  {/* 予約情報 */}
                  {element.ticketNumber && (
                    <BlockTextM>
                      <span>航空券番号：</span>
                      <span data-wovn-ignore>{element.ticketNumber}</span>
                    </BlockTextM>
                  )}
                  {element.airSeatNumber && (
                    <BlockTextM>
                      <span>座席番号：</span>
                      <span data-wovn-ignore>{element.airSeatNumber}</span>
                    </BlockTextM>
                  )}
                  {element.reservationNumber && (
                    <BlockTextM>
                      <span>予約番号：</span>
                      <span data-wovn-ignore>{element.reservationNumber}</span>
                    </BlockTextM>
                  )}
                  {element.exicReceiptNumber && (
                    <BlockTextM>
                      <span>EXICお預かり番号：</span>
                      <span data-wovn-ignore>{element.exicReceiptNumber}</span>
                    </BlockTextM>
                  )}
                  {!orderItem.isPrecedingElement(element) && (
                    <>
                      {element
                        .structuredDetail(orderItem, { elementIndex })
                        .map((l: TranslatedLine, k: number) => {
                          return l.translate ? (
                            <BlockTextM key={`t_${k}`}>
                              <span>{l.label}</span>
                              <span>{l.value}</span>
                            </BlockTextM>
                          ) : (
                            <BlockTextM key={`nt_${k}`}>
                              <span>{l.label}</span>
                              <span data-wovn-ignore>{l.value}</span>
                            </BlockTextM>
                          );
                        })}
                      <Box
                        component="table"
                        width="auto"
                        margin="0 !important"
                        border="none"
                        style={{ verticalAlign: 'top' }}
                      >
                        <tbody>
                          {orderItem.travelerShinkansenTypes(elementIndex) &&
                            orderItem.travelerShinkansenTypes(elementIndex).map((tst, i: number) => (
                              <tr key={i}>
                                {orderItem.travelerShinkansenTypes.length > 1 ? (
                                  <MainTdData>{`${i + 1}人目`}</MainTdData>
                                ) : (
                                  <MainTdData>出張者</MainTdData>
                                )}
                                <MainTd>
                                  <TranslateIgnoreText text={tst} />
                                </MainTd>
                              </tr>
                            ))}
                          {orderItem.travelerAirlineTypes &&
                            orderItem.travelerAirlineTypes.map((tat, i: number) => (
                              <tr key={i}>
                                {orderItem.travelerAirlineTypes.length > 1 ? (
                                  <MainTdData>{`${i + 1}人目`}</MainTdData>
                                ) : (
                                  <MainTdData>出張者</MainTdData>
                                )}
                                <MainTd>
                                  <TranslateIgnoreText text={tat} />
                                </MainTd>
                              </tr>
                            ))}
                        </tbody>
                      </Box>
                      {element.detailPath && (
                        <BlockTextM>
                          <span>宿泊詳細情報：</span>
                          <span>
                            <a href={element.detailPath} target="_blank" rel="noreferrer noopener">
                              詳細はコチラ
                            </a>
                          </span>
                        </BlockTextM>
                      )}
                    </>
                  )}
                </>
              ) : (
                element instanceof WifiElement && (
                  <>
                    {' '}
                    <Box
                      component="table"
                      width="auto"
                      margin="0 !important"
                      border="none"
                      style={{ verticalAlign: 'top' }}
                    >
                      <tbody>
                        {orderItem.travelerShinkansenTypes(elementIndex) &&
                          orderItem.travelerShinkansenTypes(elementIndex).map((tst, i: number) => (
                            <tr key={i}>
                              {orderItem.travelerShinkansenTypes.length > 1 ? (
                                <MainTdData>{`${i + 1}人目`}</MainTdData>
                              ) : (
                                <MainTdData>出張者</MainTdData>
                              )}
                              <MainTd>
                                <TranslateIgnoreText text={tst} />
                              </MainTd>
                            </tr>
                          ))}
                        {orderItem.travelerAirlineTypes &&
                          orderItem.travelerAirlineTypes.map((tat, i: number) => (
                            <tr key={i}>
                              {orderItem.travelerAirlineTypes.length > 1 ? (
                                <MainTdData>{`${i + 1}人目`}</MainTdData>
                              ) : (
                                <MainTdData>出張者</MainTdData>
                              )}
                              <MainTd>
                                <TranslateIgnoreText text={tat} />
                              </MainTd>
                            </tr>
                          ))}
                      </tbody>
                    </Box>
                    {element.reservationNumber && (
                      <BlockTextM>
                        <span>注文番号：</span>
                        <span data-wovn-ignore>{element.reservationNumber}</span>
                      </BlockTextM>
                    )}
                  </>
                )
              )}
              {!orderItem.isPrecedingElement(element) && (
                <>
                  <Space />
                  {element instanceof TransportElement && (
                    <>
                      {theme.themeClass === 'tabikobo' && orderItem.price?.flightPriceChangeDetail && (
                        <p>{orderItem.price.flightPriceChangeDetail}</p>
                      )}
                      {element.isRawTicketingExpired(orderItem) && (
                        <p>
                          発券期限：
                          <Deadline>{element.rawTicketingExpiredAt(orderItem)}</Deadline>
                        </p>
                      )}
                    </>
                  )}
                  {(element instanceof TransportElement || element instanceof HotelElement) &&
                    MarketLogHelper.isElementMarketLogLinkAvailable(element, theme.serviceName, trip) && (
                      <Link
                        href={MarketLogHelper.elementMarketLogPath(element, trip.id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ display: 'flex', alignItems: 'center' }}
                      >
                        マーケットログを確認
                        <div style={{ marginTop: '2px', marginLeft: '2px' }}>
                          <ExternalLinkIcon />
                        </div>
                      </Link>
                    )}
                </>
              )}
            </BlockTextM>
            <AttachFilesContainer>
              {element instanceof TransportElement &&
                sortTransportElementAttachFiles(
                  element.transportElementAttachFiles || [],
                  orderItem.travelerInformations,
                  user.id
                ).map((transportElementAttachFile: TransportElementAttachFile) => {
                  const attachFileTraveler = orderItem.travelerInformations.find(
                    traveler => traveler.id.toString() === transportElementAttachFile.travelerInformationId
                  );
                  return (
                    <div key={transportElementAttachFile.id} style={{ flex: '0 1 auto' }}>
                      <BlockTextM style={{ display: 'flex' }}>
                        <PdfIcon style={{ color: '#a72c09', marginRight: '2px' }} />
                        <A
                          href={`/trips/${transportElementAttachFile.tripId}/transport_element_attach_files/${transportElementAttachFile.id}.pdf`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ display: 'flex', alignItems: 'center' }}
                        >
                          {transportElementAttachFile.uploadFileName}
                        </A>
                      </BlockTextM>
                      <AttachFileTraveler>
                        {attachFileTraveler && ` ${attachFileTraveler.name} 様`}
                      </AttachFileTraveler>
                    </div>
                  );
                })}
            </AttachFilesContainer>
            {element instanceof WifiElement && element.elementAttachFiles?.length > 0 && (
              <AttachFilesContainer>
                {element.elementAttachFiles.map(file => (
                  <div key={file.id} style={{ flex: '0 1 auto' }}>
                    <BlockTextM style={{ display: 'flex' }}>
                      <PdfIcon style={{ color: '#a72c09', marginRight: '2px' }} />
                      <A
                        href={`/trips/${file.tripId}/element_attach_files/${file.id}.pdf`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ display: 'flex', alignItems: 'center' }}
                      >
                        {file.uploadFileName}
                      </A>
                    </BlockTextM>
                    <AttachFileTraveler>
                      {file.travelerInformationId &&
                        (() => {
                          const traveler = orderItem.travelerInformations.find(
                            t => t.id.toString() === file.travelerInformationId
                          );
                          return traveler ? `${traveler.name} 様` : '';
                        })()}
                    </AttachFileTraveler>
                  </div>
                ))}
              </AttachFilesContainer>
            )}
            {isAiTravel(serviceId) &&
              (orderItem.ticketingMethod === 'qr_east' || orderItem.ticketingMethod === 'qr_west') && (
                <BlockTextM>
                  {orderItem.ticketingMethod === 'qr_east' ? (
                    <A
                      href="https://aitravel.my.salesforce.com/sfc/p/#7F000002CrgE/a/TL000001LMUo/pwjAtO_q8Pn6wBGvZenzhYrsVGKLyVqbayim29VPb_Q"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      ※JRチケット発券マニュアル
                    </A>
                  ) : (
                    <A
                      href="https://aitravel.my.salesforce.com/sfc/p/#7F000002CrgE/a/TL000001LNIn/LHHz9yTUs8Oa9bz5k7ywE7EiAhoBqjuNDUJitIYaq80"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      ※JRチケット発券マニュアル
                    </A>
                  )}
                </BlockTextM>
              )}
          </BlockContent>
        </MainBlock>
      ))}
      {/* JRパッケージ 予約情報 */}
      {orderItem.orderItemCategory === 'jr_package' && (
        <>
          {orderItem.jrAuthCode && (
            <BlockTextM>
              <span>JR認証番号：</span>
              <span data-wovn-ignore>{orderItem.jrAuthCode}</span>
            </BlockTextM>
          )}
          {orderItem.jrPickupCode && (
            <BlockTextM>
              <span>JR引取番号：</span>
              <span data-wovn-ignore>{orderItem.jrPickupCode}</span>
            </BlockTextM>
          )}
        </>
      )}
      {orderItem.eTickets.length > 0 &&
        orderItem.eTickets.map((ticket, k: number) => (
          <p key={k}>
            <a href={ticket.link} target="_blank" rel="noopener noreferrer">
              e-ticketを表示
              <img
                className="organization-trips-detail__detail-panel__item-block__market-log-link-confirm"
                src="/images/external_link_icon.png"
                alt="link"
              />
            </a>
          </p>
        ))}
      {orderItem.price && (
        <MainBlock>
          <span data-wovn-ignore>{utils.digits(orderItem.price.totalPrice)}</span>
          <span>円</span>
        </MainBlock>
      )}
    </>
  );
};

const MainTd = styled.td`
  padding: 5px 0;
  border: none;
  vertical-align: top;
  color: ${props => props.theme.grayTextColor}
  font-weight: bold;

  ${media.sp`
    padding: 3px 0;
  `}
`;

const MainTdData = styled(MainTd)`
  width: 60px;
  color: ${props => props.theme.disabledColor};
`;

const MainBlock = styled.div``;

const BlockTitle = styled.p`
  font-size: 14px;
  font-weight: bold;
  line-height: 1.6;
  margin-bottom: 10px;

  ${media.sp`
    font-size: 12px;
  `}
`;

const BlockContent = styled.div`
  line-height: 1.8;
`;

const BlockTextL = styled.div`
  font-size: 14px;
  font-weight: bold;
  ${media.sp`
    font-size: 12px;
  `}
`;

const CanceledBlockTextL = styled.div`
  font-size: 14px;
  font-weight: bold;
  text-decoration: line-through;
  ${media.sp`
    font-size: 12px;
  `}
`;

const CancelMessage = styled.div`
  color: red;
`;

const BlockTextM = styled.div``;

const Space = styled.div`
  margin-bottom: 10px;
`;

const Room = styled.div`
  margin-top: 20px;
`;

const Deadline = styled.span`
  color: ${props => props.theme.redColor};
`;

const AttachFilesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 5px;
  justify-content: flex-start;
  align-items: flex-start;
`;

const AttachFileTraveler = styled.p`
  font-size: 10px;
  margin-left: 5px;
`;

export default withTheme(OrderItemContent);
